import { AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { fadeIn, fadeOut } from 'src/app/animations';
import { PageType } from 'src/app/models/enum/page-type.enum';
import { Job } from 'src/app/models/job.model';
import { MassCompletionOtherAssets } from 'src/app/models/mass-completion-other-assets.model';
import { PersonalDocument } from 'src/app/models/personal-document.model';
import { BitwiseHelper } from 'src/app/services/helpers/bitwise.helper';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { LookupService } from 'src/app/services/lookup.service';
import { JobService } from 'src/app/services/sections/job/job.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { SettingsPreferences } from 'src/app/services/ui/models/settings-preferences.model';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ActionsPlaceholderSettings } from 'src/app/models/enum/actions-placeholder-settings.enum';
import { JobDocumentsService } from 'src/app/services/sections/job/job-documents.service';
import { MyCloudService } from 'src/app/services/sections/job/my-cloud.service';
import { Item } from 'src/app/models/item.model';
import { some } from 'lodash-es';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';

@Component({
	templateUrl: './add-documents.component.html',
	styleUrls: ['./add-documents.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [fadeIn(300), fadeOut(300)]
})
export class AddDocumentsComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild('emptyDocumentModal', { static: true }) public emptyDocumentModal: GenericModalComponent;
	public formModel: FormGroup;

	public documentTypeOptions: { id: number, name: string }[];

	public filesToUpload: File[] = [];
	public myCloudSelectedFiles: PersonalDocument[] = [];
	public jobDocumentationAssetTypes: Item<number, string>[] = [];
	public descriptiveTags: Item<number, string>[] = [];
	public calendarBasedTags: Item<number, string>[] = [];

	public isLoading = false;

	public self = this;
	public job: Job;
	public pageType: PageType;
	public massCompletionAdditionalAssets: MassCompletionOtherAssets;
	public customerPreferences: SettingsPreferences;

	public readonly ButtonIcons = ButtonIcons;
	public readonly ActionsPlaceholderSettings = ActionsPlaceholderSettings;
	public readonly PageType = PageType;
	public readonly BitwiseHelper = BitwiseHelper;

	private unsubscriber$: Subject<void> = new Subject<void>();

	public get isActionAvailable(): boolean {
		return !this.customerPreferences?.preferences.isJobAutoSkipped &&
			!(this.pageType === PageType.Customer &&
				this.customerPreferences?.preferences.isNotificationDisabled
			);
	}

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private lookupService: LookupService,
		private jobService: JobService,
		private jobDocumentsService: JobDocumentsService,
		private myCloudService: MyCloudService
	) {


		this.formModel = new FormGroup({
			documentTypeMappingId: new FormControl(null, [Validators.required]),
			description: new FormControl(null),
			effectiveDate: new FormControl(null),
			calendarBasedTagIds: new FormControl(null),
			descriptiveTagId: new FormControl(null),
			additionalAssets: new FormControl([]),
			documentationAssetTypes: new FormControl([])
		});

		const routingData = this.router.getCurrentNavigation()?.extras.state;

		if (routingData?.selectionData) {
			this.myCloudSelectedFiles = routingData.selectionData;
			this.filesToUpload = [];
		} else {
			this.myCloudSelectedFiles = routingData?.carriedPayload?.previouslySelected || [];

			if (!this.myCloudSelectedFiles.length) {
				this.filesToUpload = routingData?.carriedPayload?.uploadFiles || [];
			}
		}

		if (routingData?.carriedPayload?.form) {
			this.formModel.patchValue(routingData.carriedPayload.form);
		}
	}

	ngOnInit(): void {
		this.job = (this.activatedRoute.snapshot.data.jobDetails || {}) as Job;
		this.pageType = this.activatedRoute.snapshot.data.pageType;
		this.customerPreferences = this.activatedRoute.snapshot.data.customerPreferences as SettingsPreferences;

		this.isLoading = true;

		forkJoin([
			this.lookupService.getDocumentTypes(TokenHelper.getToken(this.activatedRoute)),
			this.jobService.getOtherAssets(TokenHelper.getToken(this.activatedRoute)),
			this.lookupService.getDocumentationAssetTypes(TokenHelper.getToken(this.activatedRoute)),
			this.lookupService.getCalendarBasedTags(TokenHelper.getToken(this.activatedRoute))
		])
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(([types, assets, documentationAssetTypes, calendarBasedTags]) => {
				this.documentTypeOptions = types;
				this.massCompletionAdditionalAssets = assets;
				this.jobDocumentationAssetTypes = documentationAssetTypes;
				this.calendarBasedTags = calendarBasedTags;

				this.isLoading = false;
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			});
	}

	ngAfterViewInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
	}

	public onTypeChanged($event): void {
		this.formModel.patchValue({ descriptiveTagId: null });
		this.lookupService.getDescriptiveTags(TokenHelper.getToken(this.activatedRoute), $event.value).subscribe(res => this.descriptiveTags = res);
	}

	public clearEffectiveDate($event): void {
		$event.stopPropagation();
		this.formModel.patchValue({ effectiveDate: null });
	}

	public onOtherAssetChange(assetState: any, assetId: number): void {
		const additionalAssets = this.formModel.get('additionalAssets').value as number[];

		if (assetState.checked) {
			additionalAssets.push(assetId);
		} else {
			const index = additionalAssets.indexOf(assetId);
			additionalAssets.splice(index, 1);
		}

		this.formModel.patchValue({
			additionalAssets
		});
	}

	public ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public chooseFiles(files: File[]): void {
		if (!this.filesToUpload) {
			this.filesToUpload = [];
		}
		const filesToUpload = Object.values(files);
		if (some(filesToUpload, { size: 0 })) {
			this.emptyDocumentModal.open();
			return;
		}

		this.filesToUpload = [...this.filesToUpload, ...filesToUpload];
		this.myCloudSelectedFiles = [];
	}

	public clearFilesList(): void {
		this.filesToUpload = [];
	}

	public removeUploadSelection(index: number): void {
		this.filesToUpload.splice(index, 1);
	}

	public removeMyCloudSelection(index: number): void {
		this.myCloudSelectedFiles.splice(index, 1);
	}

	public submitDocuments(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		const model = this.formModel.getRawValue();
		model.isStaffMember = !!(this.pageType & PageType.AssigneeOrVendor);

		let operation: Observable<void> = null;

		if (this.filesToUpload?.length) {
			if (!Array.isArray(this.filesToUpload)) {
				this.filesToUpload = Object.values(this.filesToUpload);
			}

			operation = this.jobDocumentsService.uploadFiles(TokenHelper.getToken(this.activatedRoute), this.filesToUpload, model);
		} else if (this.myCloudSelectedFiles?.length) {
			operation = this.myCloudService.mapMyCloudDocumentsToJob(TokenHelper.getToken(this.activatedRoute), this.myCloudSelectedFiles.map(item => item.documentId), model);
		}

		if (operation) {
			operation.subscribe(
				() => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
					this.router.navigate(['../'], { relativeTo: this.activatedRoute });
				},
				(res) => {
					if (res.error.Message === 'Empty documents are not allowed.') {
						this.emptyDocumentModal.open();
					}
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				}
			);
		}
	}

	public openMyCloud(): void {
		this.navigateWithData('cloud', {
			selectionData: this.myCloudSelectedFiles,
			carriedPayload: {
				uploadFiles: this.filesToUpload,
				form: this.formModel.getRawValue(),
				previouslySelected: [...this.myCloudSelectedFiles]
			}
		});
	}

	public navigateWithData(subpage: string, state: { [key: string]: any }): void {
		this.router.navigate([subpage], { relativeTo: this.activatedRoute, state });
	}

}
